export const state = () => ({
    menuData: null,
    loading: false,
    error: null,
    selectedCalories: '1200 ккал',
    selectedDuration: 7,
    selectedDay: {
        week: 1,
        day: 1,
        day_name: 'Пн',
    },
    currentDate: null,
});

export const getters = {
    getMenuData: state => state.menuData,
    getLoading: state => state.loading,
    getError: state => state.error,
    getSelectedCalories: state => state.selectedCalories,
    getSelectedDuration: state => state.selectedDuration,
    getSelectedDay: state => state.selectedDay,
    getCurrentDate: state => state.currentDate,

    // Получение доступных опций калорийности из данных меню
    getCaloriesOptions: state => {
        if (!state.menuData || !state.menuData.menu || !state.menuData.menu.length) return [];
        return state.menuData.menu.map(item => item.calories);
    },

    // Получение доступных опций длительности для выбранной калорийности
    getDurationOptions: state => {
        if (!state.menuData || !state.menuData.menu || !state.menuData.menu.length) return [];

        // Находим меню с выбранной калорийностью
        const selectedMenu = state.menuData.menu.find(item => item.calories === state.selectedCalories);
        if (!selectedMenu) return [];

        return selectedMenu.durations.map(duration => ({
            duration: duration.duration,
            price: duration.price,
            discount: duration.discount,
        }));
    },

    // Получение списка дней для выбранной длительности и калорийности
    getDays: state => {
        if (!state.menuData || !state.menuData.menu || !state.menuData.menu.length) return [];

        // Находим меню с выбранной калорийностью
        const selectedMenu = state.menuData.menu.find(item => item.calories === state.selectedCalories);
        if (!selectedMenu) return [];

        // Находим выбранную длительность
        const selectedDurationData = selectedMenu.durations.find(d => d.duration === state.selectedDuration);
        if (!selectedDurationData) return [];

        return selectedDurationData.days;
    },

    // Получение продуктов для выбранного дня
    getProductsForSelectedDay: state => {
        if (!state.menuData || !state.menuData.menu || !state.menuData.menu.length) return [];

        // Находим меню с выбранной калорийностью
        const selectedMenu = state.menuData.menu.find(item => item.calories === state.selectedCalories);
        if (!selectedMenu) return [];

        // Находим выбранную длительность
        const selectedDurationData = selectedMenu.durations.find(d => d.duration === state.selectedDuration);
        if (!selectedDurationData) return [];

        // Находим выбранный день
        const selectedDay = selectedDurationData.days.find(
            d => d.week === state.selectedDay.week && d.day === state.selectedDay.day
        );
        if (!selectedDay) return [];

        return selectedDay.products;
    },

    // Получение описания выбранного дня
    getSelectedDayDescription: state => {
        if (!state.menuData || !state.menuData.menu || !state.menuData.menu.length) return null;

        // Находим меню с выбранной калорийностью
        const selectedMenu = state.menuData.menu.find(item => item.calories === state.selectedCalories);
        if (!selectedMenu) return null;

        // Находим выбранную длительность
        const selectedDurationData = selectedMenu.durations.find(d => d.duration === state.selectedDuration);
        if (!selectedDurationData) return null;

        // Находим выбранный день
        const selectedDay = selectedDurationData.days.find(
            d => d.week === state.selectedDay.week && d.day === state.selectedDay.day
        );
        if (!selectedDay) return null;

        return selectedDay.description || null;
    },

    // Получение цены для выбранной длительности и калорийности
    getSelectedPrice: state => {
        if (!state.menuData || !state.menuData.menu || !state.menuData.menu.length) return null;

        // Находим меню с выбранной калорийностью
        const selectedMenu = state.menuData.menu.find(item => item.calories === state.selectedCalories);
        if (!selectedMenu) return null;

        // Находим выбранную длительность
        const selectedDurationData = selectedMenu.durations.find(d => d.duration === state.selectedDuration);
        if (!selectedDurationData) return null;

        // Отладочная информация
        console.log('Данные длительности:', selectedDurationData);

        // Возвращаем данные напрямую из API
        return selectedDurationData;
    },
};

export const mutations = {
    SET_MENU_DATA(state, data) {
        state.menuData = data;

        // Если есть данные о текущей дате, устанавливаем ее
        if (data && data.current_date) {
            state.currentDate = data.current_date;
            state.selectedDay = { ...data.current_date };
        }

        // Устанавливаем начальные значения, если есть данные
        if (data && data.menu && data.menu.length > 0) {
            state.selectedCalories = data.menu[0].calories;

            if (data.menu[0].durations && data.menu[0].durations.length > 0) {
                state.selectedDuration = data.menu[0].durations[0].duration;
            }
        }
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_SELECTED_CALORIES(state, calories) {
        state.selectedCalories = calories;
    },
    SET_SELECTED_DURATION(state, duration) {
        state.selectedDuration = duration;
    },
    SET_SELECTED_DAY(state, day) {
        state.selectedDay = day;
    },
};

export const actions = {
    // Загрузка данных меню с бэкенда
    async fetchMenuData({ commit, rootGetters }) {
        try {
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);

            // Получаем ID выбранного города из хранилища
            const selectedCity = rootGetters.getSelectedCity;

            if (!selectedCity || !selectedCity.id) {
                throw new Error('Город не выбран');
            }

            // Выполняем запрос к API
            const response = await this.$axios.$get(`general/menu-calculator?locality_id=${selectedCity.id}`);

            // Сохраняем полученные данные
            commit('SET_MENU_DATA', response);

            return response;
        } catch (error) {
            console.error('Ошибка при загрузке данных меню:', error);
            commit('SET_ERROR', error.message || 'Ошибка при загрузке данных меню');
            return null;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    // Установка выбранной калорийности
    setSelectedCalories({ commit }, calories) {
        commit('SET_SELECTED_CALORIES', calories);
    },

    // Установка выбранной длительности
    setSelectedDuration({ commit }, duration) {
        commit('SET_SELECTED_DURATION', duration);
    },

    // Установка выбранного дня
    setSelectedDay({ commit }, day) {
        commit('SET_SELECTED_DAY', day);
    },
};
